import { useState, useEffect, useRef } from 'react';
import { X, Calendar, Clock, MapPin, Building, User, AlertCircle } from 'lucide-react';

const API_BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings';
const PAGINATED_BOOKINGS_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings/viewAllBoookings';
const RESOURCE_BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150';
const CUSTOMERS_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer';

const getAccessToken = () => sessionStorage.getItem('access_token');

function parseDuration(duration) {
  if (!duration) return 60;
  const match = duration.match(/PT(\d+H)?(\d+M)?/);
  if (!match) return 60;
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  return hours * 60 + minutes;
}

function getHongKongTime() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
}

function isTimePast(dateString, timeString) {
  const slotDateTime = new Date(`${dateString}T${timeString}:00`);
  return slotDateTime <= new Date();
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function parseBookingError(errorData, serviceDetails, formData) {
  const errorMessage = errorData?.error || errorData?.message || errorData?.details || '';
  const errorLower = errorMessage.toLowerCase();

  if (errorData?.errors && typeof errorData.errors === 'object') {
    const fieldErrors = Object.entries(errorData.errors)
      .map(([field, messages]) => {
        const msgArray = Array.isArray(messages) ? messages : [messages];
        return `${field}: ${msgArray.join(', ')}`;
      })
      .join('; ');

    if (fieldErrors) {
      return `Validation error: ${fieldErrors}`;
    }
  }

  if (
    errorLower.includes('duration') ||
    errorLower.includes('min') ||
    errorLower.includes('max')
  ) {
    if (serviceDetails) {
      const minDur = serviceDetails.min_duration
        ? parseDuration(serviceDetails.min_duration)
        : null;

      const maxDur = serviceDetails.max_duration
        ? parseDuration(serviceDetails.max_duration)
        : null;

      if (minDur && maxDur) {
        return `Booking duration must be between ${formatDuration(minDur)} and ${formatDuration(maxDur)}. Please adjust your start and end times.`;
      } else if (minDur) {
        return `Booking duration must be at least ${formatDuration(minDur)}. Please extend your booking time.`;
      } else if (maxDur) {
        return `Booking duration cannot exceed ${formatDuration(maxDur)}. Please shorten your booking time.`;
      }
    }

    return 'The selected booking duration is not allowed for this resource.';
  }

  if (
    errorLower.includes('schedule') ||
    errorLower.includes('available') ||
    errorLower.includes('time slot') ||
    errorLower.includes('not available')
  ) {
    return 'This resource is not available at the selected time. Please check the resource schedule.';
  }

  if (
    errorLower.includes('conflict') ||
    errorLower.includes('already booked') ||
    errorLower.includes('overlap')
  ) {
    return 'This time slot conflicts with an existing booking. Please select a different time.';
  }

  return errorMessage;
}

const addMinutesToTime = (timeString, minutes) => {
  if (!timeString) return '';

  const [hours, mins] = timeString.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;

  return `${newHours.toString().padStart(2, '0')}:${newMins
    .toString()
    .padStart(2, '0')}`;
};

const fetchAllPaginatedBookings = async () => {
  const allBookings = [];
  const limit = 100;
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetch(
      `${PAGINATED_BOOKINGS_URL}?page=${page}&limit=${limit}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch bookings: ${response.status}`);
    }

    const result = await response.json();

    const pageBookings = Array.isArray(result)
      ? result
      : Array.isArray(result.data)
        ? result.data
        : [];

    allBookings.push(...pageBookings);

    const total =
      result.meta?.total ??
      result.meta?.total_count ??
      result.meta?.count ??
      result.total ??
      result.total_count;

    totalPages =
      result.meta?.totalPages ??
      result.meta?.total_pages ??
      result.totalPages ??
      result.total_pages ??
      (total
        ? Math.ceil(total / limit)
        : pageBookings.length < limit
          ? page
          : page + 1);

    if (pageBookings.length === 0) {
      break;
    }

    page++;
  }

  return allBookings;
};

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  confirmColor = '#db6969',
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div
        className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        style={{ fontFamily: 'Inter' }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600">{message}</p>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelText}
          </button>

          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: confirmColor }}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AddBookingForm({ onClose, onBookingCreated }) {
  const [formData, setFormData] = useState({
    location_id: '80c10e0e-3ead-4740-a2a6-574354bb9a77',
    resource_id: '',
    service_id: '',
    starts_at: '',
    ends_at: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    customer_id: '',
    customer_name: '',
    price: '0.000',
    notes: '',
    is_past_booking: false,
  });

  const [loggedInUser, setLoggedInUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [resources, setResources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingResourceData, setLoadingResourceData] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [validDates, setValidDates] = useState([]);
  const [resourceDetails, setResourceDetails] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  const [customerDetails, setCustomerDetails] = useState(null);
  const [showUseCreditsModal, setShowUseCreditsModal] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsApplied, setCreditsApplied] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [timeCreditsDeducted, setTimeCreditsDeducted] = useState(0);
  const [moneyCreditsDeducted, setMoneyCreditsDeducted] = useState(0);

  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  const [isResourceDropdownOpen, setIsResourceDropdownOpen] = useState(false);
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [error]);

  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');

    if (storedUser?.email) {
      setLoggedInUser(storedUser);
    }
  }, []);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        setLoadingData(true);

        const resourcesRes = await fetch(
          `${RESOURCE_BASE_URL}/resource/viewAllResource`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (resourcesRes.ok) {
          const resourcesData = await resourcesRes.json();

          setResources(
            Array.isArray(resourcesData)
              ? resourcesData
              : resourcesData.data || []
          );
        }

        const custRes = await fetch(
          `${CUSTOMERS_URL}/getAllCustomer`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (custRes.ok) {
          const custData = await custRes.json();

          setCustomers(
            (custData.customers || []).map(c => ({
              id: c._id,
              name: c.fullName,
              email: c.email,
            }))
          );
        }
      } catch (err) {
        setError(err.message || 'Failed to load form data.');
      } finally {
        setLoadingData(false);
      }
    };

    loadFormData();
  }, []);

  useEffect(() => {
    if (!formData.customer_id) {
      setCustomerDetails(null);
      setCreditsApplied(false);
      setDiscountedPrice(null);
      setTimeCreditsDeducted(0);
      setMoneyCreditsDeducted(0);
      return;
    }

    const fetchCustomerProfile = async () => {
      try {
        const res = await fetch(
          `${CUSTOMERS_URL}/getCustomer/${formData.customer_id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (res.ok) {
          const data = await res.json();
          setCustomerDetails(data);
        }
      } catch (err) {
        console.error('Failed to fetch customer details:', err);
      }
    };

    fetchCustomerProfile();
  }, [formData.customer_id]);

  useEffect(() => {
    const loadResourceData = async () => {
      if (!formData.resource_id) {
        setFormData(prev => ({
          ...prev,
          service_id: '',
          price: '0.000',
        }));

        setServiceDetails(null);
        setScheduleBlocks([]);
        setValidDates([]);
        setResourceDetails(null);

        return;
      }

      try {
        setLoadingResourceData(true);

        const selectedResource = resources.find(
          r => r.id === formData.resource_id
        );

        if (!selectedResource) return;

        const resourceRes = await fetch(
          `${RESOURCE_BASE_URL}/resource/getResourceByName/${encodeURIComponent(
            selectedResource.name
          )}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (resourceRes.ok) {
          const resourceData = await resourceRes.json();
          setResourceDetails(resourceData.resource);
        }

        let serviceId;

        const serviceIdRes = await fetch(
          `${RESOURCE_BASE_URL}/service/getServiceIdByResourceId/${formData.resource_id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (serviceIdRes.ok) {
          const serviceData = await serviceIdRes.json();

          serviceId = serviceData.serviceId;

          setFormData(prev => ({
            ...prev,
            service_id: serviceId,
          }));
        }

        if (serviceId) {
          const serviceRes = await fetch(
            `${RESOURCE_BASE_URL}/service/viewServiceByServiceId/${serviceId}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getAccessToken()}`,
              },
            }
          );

          if (serviceRes.ok) {
            setServiceDetails(await serviceRes.json());
          }
        }

        const scheduleRes = await fetch(
          `${RESOURCE_BASE_URL}/resource/getResourceScheduleInfo/${encodeURIComponent(
            selectedResource.name
          )}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (scheduleRes.ok) {
          const scheduleData = await scheduleRes.json();

          setScheduleBlocks(
            scheduleData.data?.schedule_blocks || []
          );

          const blocks = scheduleData.data?.schedule_blocks || [];
          const recurringSchedule =
            scheduleData.data?.recurring_schedule || {};

          if (blocks.length > 0) {
            const startDate = recurringSchedule?.start_date
              ? new Date(recurringSchedule.start_date)
              : new Date();

            const endDate = recurringSchedule?.end_date
              ? new Date(recurringSchedule.end_date)
              : new Date(
                  Date.now() + 365 * 24 * 60 * 60 * 1000
                );

            const validDays = new Set(
              blocks.map(block => block.weekday.toLowerCase())
            );

            const dates = [];

            for (
              let d = new Date(startDate);
              d <= endDate;
              d.setDate(d.getDate() + 1)
            ) {
              if (
                validDays.has(
                  d
                    .toLocaleDateString('en-US', {
                      weekday: 'long',
                    })
                    .toLowerCase()
                )
              ) {
                dates.push(
                  new Date(d).toISOString().split('T')[0]
                );
              }
            }

            setValidDates(dates);
          } else {
            setValidDates([]);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingResourceData(false);
      }
    };

    loadResourceData();
  }, [formData.resource_id, resources]);

  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (
        !formData.resource_id ||
        !formData.start_date ||
        !formData.service_id
      ) {
        setAvailableSlots([]);
        return;
      }

      try {
        setLoadingSlots(true);

        const allBookings = await fetchAllPaginatedBookings();

        const resourceBookings = allBookings.filter(
          booking =>
            booking.resource?.id === formData.resource_id
        );

        const dayBookings = resourceBookings.filter(booking => {
          if (!booking.starts_at) return false;

          const bookingDateStr = new Date(
            booking.starts_at
          ).toLocaleDateString('en-CA');

          return (
            bookingDateStr === formData.start_date &&
            !booking.is_canceled
          );
        });

        const selectedDate = new Date(formData.start_date);

        const weekday = selectedDate
          .toLocaleDateString('en-US', {
            weekday: 'long',
          })
          .toLowerCase();

        const dayBlocks = scheduleBlocks.filter(
          block => block.weekday === weekday
        );

        const interval = serviceDetails?.bookable_interval
          ? parseDuration(serviceDetails.bookable_interval)
          : serviceDetails?.duration_step
            ? parseDuration(serviceDetails.duration_step)
            : 15;

        const slots = [];

        dayBlocks.forEach(block => {
          const [startHour, startMin] = block.start_time
            .split(':')
            .map(Number);

          const [endHour, endMin] = block.end_time
            .split(':')
            .map(Number);

          for (
            let time = startHour * 60 + startMin;
            time < endHour * 60 + endMin;
            time += interval
          ) {
            const hour = Math.floor(time / 60);
            const minute = time % 60;

            const timeStr = `${hour
              .toString()
              .padStart(2, '0')}:${minute
              .toString()
              .padStart(2, '0')}`;

            const isBooked = dayBookings.some(booking => {
              const bookingStart = new Date(
                booking.starts_at
              );

              const bookingEnd = new Date(
                booking.ends_at
              );

              const slotStartTime = new Date(
                formData.start_date +
                  'T' +
                  timeStr +
                  ':00+07:00'
              );

              const slotDuration = serviceDetails?.duration
                ? parseDuration(serviceDetails.duration)
                : interval;

              const slotEndTime = new Date(
                slotStartTime.getTime() +
                  slotDuration * 60 * 1000
              );

              return (
                slotStartTime < bookingEnd &&
                slotEndTime > bookingStart
              );
            });

            slots.push({
              time: timeStr,
              available: !isBooked,
            });
          }
        });

        setAvailableSlots(slots);
      } catch (error) {
        console.error(
          'Failed to load availability:',
          error
        );

        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadAvailableSlots();
  }, [
    formData.resource_id,
    formData.start_date,
    formData.service_id,
    scheduleBlocks,
    serviceDetails,
  ]);

  const calculateBasePrice = (
    startDate,
    endDate,
    startTime,
    endTime,
    rates,
    isMember
  ) => {
    try {
      const start = new Date(
        `${startDate}T${startTime}:00`
      );

      const end = new Date(
        `${endDate}T${endTime}:00`
      );

      const totalMinutes =
        (end - start) / (1000 * 60);

      if (totalMinutes <= 0) return 0;

      const totalHours = totalMinutes / 60;
      const totalDays = totalMinutes / (60 * 24);
      const totalWeeks = totalDays / 7;

      const validRates = rates.filter(rate => {
        const type = rate.userType || 'Both';

        if (isMember) {
          return (
            type === 'Both' ||
            type === 'Members Only'
          );
        }

        return (
          type === 'Both' ||
          type === 'Contacts Only' ||
          type === 'Non-Members'
        );
      });

      let selectedRate = null;
      let calculatedPrice = 0;

      if (totalWeeks >= 1) {
        selectedRate = validRates.find(
          r => r.renewal_type === 'every week'
        );

        if (selectedRate) {
          calculatedPrice =
            selectedRate.amount *
            Math.ceil(totalWeeks);
        }
      }

      if (!selectedRate && totalDays >= 1) {
        selectedRate = validRates.find(
          r => r.renewal_type === 'every day'
        );

        if (selectedRate) {
          calculatedPrice =
            selectedRate.amount *
            Math.ceil(totalDays);
        }
      }

      if (!selectedRate) {
        selectedRate = validRates.find(
          r => r.renewal_type === 'every hour'
        );

        if (selectedRate) {
          calculatedPrice =
            selectedRate.amount *
            Math.ceil(totalHours);
        }
      }

      if (!selectedRate && validRates.length > 0) {
        selectedRate = validRates[0];

        calculatedPrice =
          selectedRate.amount *
          (selectedRate.renewal_type === 'every use'
            ? 1
            : Math.ceil(totalHours));
      }

      return calculatedPrice;
    } catch (error) {
      console.error('Price Calc Error', error);
      return 0;
    }
  };

  useEffect(() => {
    if (
      !resourceDetails?.metadata?.rates ||
      resourceDetails.metadata.rates.length === 0
    ) {
      return;
    }

    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_time &&
      formData.end_time
    ) {
      const price = calculateBasePrice(
        formData.start_date,
        formData.end_date,
        formData.start_time,
        formData.end_time,
        resourceDetails.metadata.rates,
        customerDetails?.customer?.isMember || false
      );

      setFormData(prev => ({
        ...prev,
        price: price.toFixed(3),
      }));

      setCreditsApplied(false);
      setDiscountedPrice(null);
      setTimeCreditsDeducted(0);
      setMoneyCreditsDeducted(0);
    }
  }, [
    formData.start_date,
    formData.end_date,
    formData.start_time,
    formData.end_time,
    resourceDetails,
    customerDetails,
  ]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    setError('');
  };

  const validateForm = () => {
    if (!formData.customer_id) {
      return 'Please select a customer';
    }

    if (!formData.location_id) {
      return 'Please select a location';
    }

    if (!formData.resource_id) {
      return 'Please select a resource';
    }

    if (!formData.start_date || !formData.end_date) {
      return 'Please select start and end dates';
    }

    if (!formData.start_time || !formData.end_time) {
      return 'Please select start and end times';
    }

    if (formData.end_date < formData.start_date) {
      return 'End date cannot be before start date';
    }

    if (
      formData.start_date === formData.end_date &&
      formData.start_time >= formData.end_time
    ) {
      return 'End time must be after start time';
    }

    if (
      !formData.is_past_booking &&
      validDates.length > 0 &&
      !validDates.includes(formData.start_date)
    ) {
      return 'Selected date is not available for this resource';
    }

    const startSlot = availableSlots.find(
      s => s.time === formData.start_time
    );

    if (startSlot && !startSlot.available) {
      return 'Selected start time is not available.';
    }

    const [startHour, startMin] =
      formData.start_time.split(':').map(Number);

    const [endHour, endMin] =
      formData.end_time.split(':').map(Number);

    const startTotalMins =
      startHour * 60 + startMin;

    const endTotalMins =
      endHour * 60 + endMin;

    for (let slot of availableSlots) {
      const [slotHour, slotMin] =
        slot.time.split(':').map(Number);

      const slotTotalMins =
        slotHour * 60 + slotMin;

      if (
        slotTotalMins >= startTotalMins &&
        slotTotalMins < endTotalMins &&
        !slot.available
      ) {
        return 'One or more time slots in your selected range are already booked.';
      }
    }

    if (
      !formData.is_past_booking &&
      isTimePast(
        formData.start_date,
        formData.start_time
      )
    ) {
      return 'Cannot book a time in the past';
    }

    return null;
  };

  const handleUseCredits = async () => {
    if (
      !formData.customer_id ||
      !formData.resource_id
    ) {
      return;
    }

    setCreditsLoading(true);

    try {
      const selectedResource = resources.find(
        r => r.id === formData.resource_id
      );

      const timezone =
        locations.find(
          l => l.id === formData.location_id
        )?.time_zone || 'Asia/Jakarta';

      const offset = getTimezoneOffset(timezone);

      const starts_at = `${formData.start_date}T${formData.start_time}:00${offset}`;
      const ends_at = `${formData.end_date}T${formData.end_time}:00${offset}`;

      const res = await fetch(
        'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer/consumeCustomerBenefits',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            customerId:
              customerDetails?.customer?.id ||
              formData.customer_id,
            resourceName: selectedResource?.name,
            startTime: starts_at,
            endTime: ends_at,
          }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setDiscountedPrice(
          data.data.final_price_due.toFixed(3)
        );

        setTimeCreditsDeducted(
          data.data.time_credits_deducted || 0
        );

        setMoneyCreditsDeducted(
          data.data.money_credits_deducted || 0
        );

        setCreditsApplied(true);
        setShowUseCreditsModal(false);
      } else {
        throw new Error(
          data.message ||
            'Failed to calculate credits preview'
        );
      }
    } catch (e) {
      setError(e.message);
      setShowUseCreditsModal(false);
    } finally {
      setCreditsLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.service_id) {
      setError('Service information is missing.');
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const selectedLocation = locations.find(
        loc => loc.id === formData.location_id
      );

      const timezone =
        selectedLocation?.time_zone ||
        'Asia/Jakarta';

      const offset = getTimezoneOffset(timezone);

      const starts_at = `${formData.start_date}T${formData.start_time}:00${offset}`;
      const ends_at = `${formData.end_date}T${formData.end_time}:00${offset}`;

      const selectedCustomer = customers.find(
        c => c.id === formData.customer_id
      );

      const customerEmail =
        selectedCustomer?.email ||
        formData.customer_name.trim();

      let customerName =
        selectedCustomer?.name ||
        formData.customer_name.trim();

      const finalSubmissionPrice =
        creditsApplied &&
        discountedPrice !== null
          ? discountedPrice
          : formData.price;

      const bookingData = {
        resource_id: formData.resource_id,
        service_id: formData.service_id,
        location_id: formData.location_id,
        starts_at,
        ends_at,
        price: finalSubmissionPrice,
        customer_id: formData.customer_id,
        customer_name: customerName,
        notes: formData.notes.trim(),
        is_past_booking: formData.is_past_booking,
        time_credits_deducted: creditsApplied
          ? timeCreditsDeducted
          : 0,
        money_credits_deducted: creditsApplied
          ? moneyCreditsDeducted
          : 0,
      };

      console.log(
        'Submitting Booking Data:',
        bookingData
      );

      const response = await fetch(
        `${API_BASE_URL}/createBooking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify(bookingData),
        }
      );

      if (!response.ok) {
        let errorData = {};

        try {
          errorData = await response.json();
        } catch (e) {}

        throw new Error(
          parseBookingError(
            errorData,
            serviceDetails,
            formData
          )
        );
      }

      if (onBookingCreated) {
        onBookingCreated();
      }
    } catch (err) {
      setError(
        err.message || 'Failed to create booking.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getTimezoneOffset = () => {
    const offsetMinutes =
      new Date().getTimezoneOffset();

    const sign =
      offsetMinutes <= 0 ? '+' : '-';

    const abs = Math.abs(offsetMinutes);

    const h = String(
      Math.floor(abs / 60)
    ).padStart(2, '0');

    const m = String(
      abs % 60
    ).padStart(2, '0');

    return `${sign}${h}:${m}`;
  };

  const isDateDisabled = dateString => {
    if (validDates.length === 0) {
      return false;
    }

    return !validDates.includes(dateString);
  };

  if (loadingData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>

          <p className="mt-4 text-gray-600">
            Loading form data...
          </p>
        </div>
      </div>
    );
  }

  const hasAvailableCredits =
    customerDetails?.summary &&
    (
      customerDetails.summary
        .total_time_credit_minutes > 0 ||
      customerDetails.summary
        .total_money_credit > 0
    );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      style={{ fontFamily: 'Inter' }}
    >
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">

        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-blue-600" />

            <h2 className="text-xl font-semibold text-gray-900">
              Add New Booking
            </h2>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div
              ref={errorRef}
              className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2 mb-6"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />

              <div className="text-sm text-red-600">
                {error}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">

            <div className="col-span-1 lg:col-span-2 space-y-6">

              <div className="relative">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  Customer *
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setIsCustomerDropdownOpen(
                      !isCustomerDropdownOpen
                    )
                  }
                  className="w-full text-left truncate px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {formData.customer_id
                    ? customers.find(
                        c =>
                          c.id ===
                          formData.customer_id
                      )?.email
                      ? `${formData.customer_name} (${customers.find(
                          c =>
                            c.id ===
                            formData.customer_id
                        )?.email})`
                      : formData.customer_name
                    : 'Select a customer'}
                </button>

                {isCustomerDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() =>
                        setIsCustomerDropdownOpen(false)
                      }
                    />

                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col max-h-[50vh]">

                      <div className="p-2 border-b border-gray-200 sticky top-0 bg-white rounded-t-md z-10">
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={customerSearchQuery}
                          onChange={e =>
                            setCustomerSearchQuery(
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>

                      <div className="overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            handleChange(
                              'customer_id',
                              ''
                            );

                            handleChange(
                              'customer_name',
                              ''
                            );

                            setIsCustomerDropdownOpen(
                              false
                            );

                            setCustomerSearchQuery('');
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
                        >
                          Select a customer (Clear)
                        </button>

                        {customers
                          .filter(c => {
                            const searchStr =
                              customerSearchQuery.toLowerCase();

                            return (
                              (c.name || '')
                                .toLowerCase()
                                .includes(searchStr) ||
                              (c.email || '')
                                .toLowerCase()
                                .includes(searchStr)
                            );
                          })
                          .map(customer => {
                            const displayText =
                              customer.email
                                ? `${customer.name} (${customer.email})`
                                : customer.name;

                            return (
                              <button
                                key={customer.id}
                                type="button"
                                onClick={() => {
                                  handleChange(
                                    'customer_id',
                                    customer.id
                                  );

                                  handleChange(
                                    'customer_name',
                                    customer.name || ''
                                  );

                                  setIsCustomerDropdownOpen(
                                    false
                                  );

                                  setCustomerSearchQuery('');
                                }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors truncate ${
                                  formData.customer_id ===
                                  customer.id
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-700'
                                }`}
                                title={displayText}
                              >
                                {displayText}
                              </button>
                            );
                          })}

                        {customers.filter(
                          c =>
                            (c.name || '')
                              .toLowerCase()
                              .includes(
                                customerSearchQuery.toLowerCase()
                              ) ||
                            (c.email || '')
                              .toLowerCase()
                              .includes(
                                customerSearchQuery.toLowerCase()
                              )
                        ).length === 0 && (
                          <div className="px-3 py-4 text-sm text-center text-gray-500">
                            No customers found.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="relative">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4" />
                  Resource *
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setIsResourceDropdownOpen(
                      !isResourceDropdownOpen
                    )
                  }
                  className="w-full text-left truncate px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {formData.resource_id
                    ? resources.find(
                        r =>
                          r.id ===
                          formData.resource_id
                      )?.name ||
                      'Unknown Resource'
                    : 'Select a resource'}
                </button>

                {isResourceDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() =>
                        setIsResourceDropdownOpen(false)
                      }
                    />

                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col max-h-[50vh]">

                      <div className="p-2 border-b border-gray-200 sticky top-0 bg-white rounded-t-md z-10">
                        <input
                          type="text"
                          placeholder="Search resources..."
                          value={resourceSearchQuery}
                          onChange={e =>
                            setResourceSearchQuery(
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>

                      <div className="overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            handleChange(
                              'resource_id',
                              ''
                            );

                            setIsResourceDropdownOpen(
                              false
                            );

                            setResourceSearchQuery('');
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
                        >
                          Select a resource (Clear)
                        </button>

                        {resources
                          .filter(r =>
                            (r.name || '')
                              .toLowerCase()
                              .includes(
                                resourceSearchQuery.toLowerCase()
                              )
                          )
                          .map(resource => (
                            <button
                              key={resource.id}
                              type="button"
                              onClick={() => {
                                handleChange(
                                  'resource_id',
                                  resource.id
                                );

                                setIsResourceDropdownOpen(
                                  false
                                );

                                setResourceSearchQuery('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors truncate ${
                                formData.resource_id ===
                                resource.id
                                  ? 'bg-blue-50 text-blue-700 font-medium'
                                  : 'text-gray-700'
                              }`}
                              title={resource.name}
                            >
                              {resource.name}
                            </button>
                          ))}

                        {resources.filter(r =>
                          (r.name || '')
                            .toLowerCase()
                            .includes(
                              resourceSearchQuery.toLowerCase()
                            )
                        ).length === 0 && (
                          <div className="px-3 py-4 text-sm text-center text-gray-500">
                            No resources found.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4" />
                  Location *
                </label>

                <input
                  type="text"
                  value="Continuum"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {loadingResourceData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>

                  <span className="text-sm text-blue-800">
                    Loading resource details...
                  </span>
                </div>
              )}

              {formData.price &&
                parseFloat(formData.price) > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">

                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600 font-medium">
                          Base Price
                        </p>

                        <p
                          className={`text-lg font-bold ${
                            creditsApplied &&
                            discountedPrice !== null
                              ? 'text-gray-400 line-through'
                              : 'text-blue-900'
                          }`}
                        >
                          IDR{' '}
                          {parseFloat(
                            formData.price
                          ).toLocaleString()}
                        </p>
                      </div>

                      {creditsApplied &&
                        discountedPrice !== null && (
                          <div className="text-right">
                            <p className="text-sm text-green-600 font-bold tracking-wide">
                              Discounted Final Price
                            </p>

                            <p className="text-2xl font-black text-green-700">
                              IDR{' '}
                              {parseFloat(
                                discountedPrice
                              ).toLocaleString()}
                            </p>
                          </div>
                        )}
                    </div>

                    {!creditsApplied && (
                      <div className="mt-4 pt-4 border-t border-blue-200 flex justify-between items-center">
                        <p className="text-sm text-blue-800 font-medium">
                          {hasAvailableCredits
                            ? 'You have active credits available.'
                            : 'No active credits in wallet.'}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            setShowUseCreditsModal(true)
                          }
                          disabled={!hasAvailableCredits}
                          className="px-4 py-2 bg-[#1A5090] text-white text-sm font-semibold rounded-lg hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply Credits
                        </button>
                      </div>
                    )}
                  </div>
                )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4" />
                    Start Date *
                  </label>

                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={e =>
                      handleChange(
                        'start_date',
                        e.target.value
                      )
                    }
                    min={
                      formData.is_past_booking
                        ? undefined
                        : new Date()
                            .toISOString()
                            .split('T')[0]
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer text-base sm:text-sm"
                    required
                  />

                  {!formData.is_past_booking &&
                    validDates.length > 0 &&
                    isDateDisabled(
                      formData.start_date
                    ) && (
                      <p className="text-xs text-red-600 mt-1">
                        This date is not available
                      </p>
                    )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4" />
                    End Date *
                  </label>

                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={e =>
                      handleChange(
                        'end_date',
                        e.target.value
                      )
                    }
                    min={formData.start_date}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4" />
                    Start time *

                    {loadingSlots && (
                      <span className="text-xs text-gray-500">
                        (Loading...)
                      </span>
                    )}
                  </label>

                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={e => {
                      const time = e.target.value;

                      handleChange(
                        'start_time',
                        time
                      );

                      if (
                        serviceDetails?.duration &&
                        time
                      ) {
                        handleChange(
                          'end_time',
                          addMinutesToTime(
                            time,
                            parseDuration(
                              serviceDetails.duration
                            )
                          )
                        );
                      }
                    }}
                    disabled={loadingSlots}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer ${
                      loadingSlots
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'bg-white'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4" />
                    End time *
                  </label>

                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={e =>
                      handleChange(
                        'end_time',
                        e.target.value
                      )
                    }
                    disabled={loadingSlots}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer ${
                      loadingSlots
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'bg-white'
                    }`}
                    required
                  />
                </div>
              </div>

              {!loadingSlots &&
                formData.resource_id &&
                formData.start_date && (
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 4, 8].map(hours => (
                      <button
                        key={hours}
                        type="button"
                        onClick={() =>
                          handleChange(
                            'end_time',
                            addMinutesToTime(
                              formData.start_time,
                              hours * 60
                            )
                          )
                        }
                        className="px-4 py-2 text-sm rounded-lg transition-all hover:bg-blue-700 bg-[#1A5090] text-white"
                      >
                        {hours === 1
                          ? '1 hour'
                          : `${hours} hours`}
                      </button>
                    ))}
                  </div>
                )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note / Remarks
                </label>

                <textarea
                  value={formData.notes}
                  onChange={e =>
                    handleChange(
                      'notes',
                      e.target.value
                    )
                  }
                  placeholder="Add any notes or remarks for this booking..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none min-h-[80px]"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_past_booking"
                  checked={formData.is_past_booking}
                  onChange={e =>
                    handleChange(
                      'is_past_booking',
                      e.target.checked
                    )
                  }
                  className="w-4 h-4 border border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-blue-500"
                />

                <label
                  htmlFor="is_past_booking"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Past Booking
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || loadingSlots}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 bg-[#184AC0]"
                >
                  {loading
                    ? 'Creating...'
                    : 'Create Booking'}
                </button>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-base font-medium">
                Availability
              </h3>

              {formData.resource_id &&
              formData.start_date ? (
                <>
                  <div className="bg-white border rounded overflow-y-auto h-[400px] w-full lg:w-[250px] border-gray-200">
                    <style>{`
                      .unavailable-pattern {
                        background-image: repeating-linear-gradient(45deg, #9CA3AF 0, #9CA3AF 1px, transparent 0, transparent 50%);
                        background-size: 10px 10px;
                        opacity: 0.3;
                      }
                    `}</style>

                    <div className="p-3">
                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-8">
                          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />

                          <p className="text-sm text-gray-600">
                            No available slots
                          </p>
                        </div>
                      ) : (
                        availableSlots.map(slot => {
                          const isPast = isTimePast(
                            formData.start_date,
                            slot.time
                          );

                          const isSelected =
                            formData.start_time ===
                            slot.time;

                          return (
                            <div
                              key={slot.time}
                              className="flex items-center gap-3 mb-1 cursor-pointer"
                              onClick={() => {
                                if (
                                  slot.available &&
                                  !isPast
                                ) {
                                  handleChange(
                                    'start_time',
                                    slot.time
                                  );

                                  if (
                                    serviceDetails?.duration
                                  ) {
                                    handleChange(
                                      'end_time',
                                      addMinutesToTime(
                                        slot.time,
                                        parseDuration(
                                          serviceDetails.duration
                                        )
                                      )
                                    );
                                  }
                                }
                              }}
                            >
                              <span className="text-left text-[11px] text-gray-500 font-medium w-[60px] flex-shrink-0">
                                {slot.time}
                              </span>

                              <div
                                className={`flex-1 rounded-sm h-[24px] flex items-center px-1 transition-all ${
                                  !slot.available
                                    ? 'unavailable-pattern border-red-600 bg-red-500'
                                    : isSelected
                                      ? 'bg-[#1A5090] border-blue-700'
                                      : 'bg-gray-100 border-gray-200'
                                } ${
                                  isPast
                                    ? 'opacity-50'
                                    : ''
                                }`}
                              >
                                {isSelected && (
                                  <span className="text-white font-medium text-[9px]">
                                    Selected
                                  </span>
                                )}

                                {!slot.available &&
                                  !isSelected && (
                                    <span className="text-white font-medium text-[9px]">
                                      Booked
                                    </span>
                                  )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="mt-3 space-y-1 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#1A5090] rounded-sm"></div>
                      <span>Selected</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#db6969] rounded-sm"></div>
                      <span>Booked</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded-sm"></div>
                      <span>Available</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 unavailable-pattern bg-gray-400 rounded-sm"></div>
                      <span>Past</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-sm text-gray-500">
                  Select a resource and date to view availability
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        confirmColor="#1A5090"
        isOpen={showUseCreditsModal}
        onClose={() =>
          setShowUseCreditsModal(false)
        }
        onConfirm={handleUseCredits}
        title="Apply Customer Credits"
        message="Are you sure you want to calculate and apply credits for this booking? Note: Credits will be permanently deducted only after you click 'Create Booking'."
        confirmText="Yes, Preview & Apply"
        cancelText="Cancel"
        isLoading={creditsLoading}
      />
    </div>
  );
}